import React from 'react';
import { motion } from 'framer-motion';

const Investors: React.FC = () => {
  return (
    <div className="py-16 bg-black relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gold-950/30 to-black/90"
        animate={{ 
          backgroundPosition: ['center top', 'center bottom', 'center top'],
        }}
        transition={{ 
          duration: 25, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        style={{ 
          backgroundSize: 'auto 200%',
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="max-w-4xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gold-400 mb-6">Invest in Setora</h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">Setora is a custom software studio with a growing portfolio of shipped client work, a revenue-generating product in PlayMaker Pics, and deep expertise applying AI to real small-business problems. We offer investors a chance to back a profitable, services-and-products software company built on proven delivery.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <motion.div 
            className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-6 shadow-xl shadow-gold-900/20 backdrop-blur-sm"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileHover={{ y: -5, boxShadow: '0 10px 30px -15px rgba(211, 158, 61, 0.3)' }}
          >
            <div className="w-16 h-16 bg-gold-400/20 rounded-2xl flex items-center justify-center mb-4 mx-auto text-gold-400">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <h3 className="text-xl font-bold text-gold-400 mb-3 text-center">Proven Delivery</h3>
            <p className="text-gray-300 text-sm text-center">A growing portfolio of shipped software across trades, healthcare, retail, hospitality, and finance — plus our own live product, PlayMaker Pics. Real clients, real revenue, real traction.</p>
          </motion.div>
          <motion.div 
            className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-6 shadow-xl shadow-gold-900/20 backdrop-blur-sm"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            whileHover={{ y: -5, boxShadow: '0 10px 30px -15px rgba(211, 158, 61, 0.3)' }}
          >
            <div className="w-16 h-16 bg-gold-400/20 rounded-2xl flex items-center justify-center mb-4 mx-auto text-gold-400">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
            </div>
            <h3 className="text-xl font-bold text-gold-400 mb-3 text-center">Massive Market Potential</h3>
            <p className="text-gray-300 text-sm text-center">The global AI market is projected to reach $190 billion by 2025. Setora is positioned to capture a significant share by targeting creative industries with high growth potential.</p>
          </motion.div>
          <motion.div 
            className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-6 shadow-xl shadow-gold-900/20 backdrop-blur-sm"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            whileHover={{ y: -5, boxShadow: '0 10px 30px -15px rgba(211, 158, 61, 0.3)' }}
          >
            <div className="w-16 h-16 bg-gold-400/20 rounded-2xl flex items-center justify-center mb-4 mx-auto text-gold-400">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9v12a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h2m5.66 0H14a2 2 0 012 2v3.34l1 1L17 9v10m1-3l2-2m0 0l2 2m-2-2v2"></path></svg>
            </div>
            <h3 className="text-xl font-bold text-gold-400 mb-3 text-center">Dual Growth Engine</h3>
            <p className="text-gray-300 text-sm text-center">Services fund the studio while productized tools and AI offerings — distilled from real client work — create recurring, scalable revenue across diverse verticals.</p>
          </motion.div>
        </div>

        <motion.div 
          className="bg-gray-900/80 border border-gold-800/30 rounded-3xl p-8 mb-16 shadow-xl shadow-gold-900/20 backdrop-blur-sm"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-gold-400 mb-6 text-center">Why Invest in Setora?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-gold-400 rounded-full mr-3 flex items-center justify-center text-black text-sm font-bold">1</span>
                  <div>
                    <h3 className="text-gold-400 font-semibold">AI-Native Studio</h3>
                    <p className="text-gray-300 text-sm">Setora builds AI deeply into the software it ships, giving small businesses capabilities normally reserved for the enterprise — a durable edge in a fast-growing market.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-gold-400 rounded-full mr-3 flex items-center justify-center text-black text-sm font-bold">2</span>
                  <div>
                    <h3 className="text-gold-400 font-semibold">Proven Product-Market Fit</h3>
                    <p className="text-gray-300 text-sm">PlayMaker Pics has validated our approach, showing strong user adoption and customer satisfaction in real-world applications.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-gold-400 rounded-full mr-3 flex items-center justify-center text-black text-sm font-bold">3</span>
                  <div>
                    <h3 className="text-gold-400 font-semibold">Scalable Business Model</h3>
                    <p className="text-gray-300 text-sm">Our SaaS platform is designed for scalability, with multiple revenue streams including subscriptions, licensing, and on-site services.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="flex items-center justify-center">
              <motion.div 
                className="w-48 h-48 bg-gold-500/10 rounded-full flex items-center justify-center"
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              >
                <div className="w-36 h-36 bg-gold-400/20 rounded-full flex items-center justify-center text-gold-400 text-4xl font-bold">AI</div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.a 
            href="/contact" 
            className="inline-block px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:to-gold-500 text-black font-semibold rounded-xl shadow-lg shadow-gold-500/30 transition-all duration-300 pulse-glow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Investor Relations
          </motion.a>
        </motion.div>
      </div>
      <motion.div 
        className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, -30, 0],
          y: [0, 30, 0],
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
      <motion.div 
        className="absolute bottom-0 right-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, 30, 0],
          y: [0, -30, 0],
        }}
        transition={{ 
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );
};

export default Investors; 